import { AnimatePresence, motion, useDragControls } from 'motion/react';
import { type ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';

import { useIsDesktop } from '@/shared/lib/use-media-query';
import { cn } from '@/shared/lib/cn';

interface SheetProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: ReactNode;
  /** Прилипшая к низу панель действий — не уезжает при скролле содержимого. */
  footer?: ReactNode;
  /** Доля высоты экрана. `auto` — по содержимому, но не выше 92%. */
  height?: 'auto' | 'half' | 'full';
}

/**
 * Нижняя шторка — основной способ показать форму или детали на мобильном.
 *
 * Выбрана вместо центрального модального окна осознанно: шторка появляется
 * рядом с большим пальцем, закрывается свайпом вниз и не перекрывает контекст
 * экрана целиком. Это стандарт Material 3 и iOS.
 */
export function Sheet({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  height = 'auto',
}: SheetProps) {
  // Пока шторка открыта, фон не должен прокручиваться под ней.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  // Escape закрывает шторку — важно для работы с внешней клавиатурой.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  /*
    Шторку тянут за ручку и заголовок, а не за любое место.

    Перетаскиваемому элементу библиотека анимации сама прописывает запрет
    выделения, запрет контекстного меню и своё правило касаний. Пока
    перетаскивалась вся шторка, всё это получало каждое поле формы внутри неё
    — и на Android касание переставало переставлять курсор: клавиатура Samsung
    продолжала писать в конец названия и заметки, куда бы человек ни нажал.
    Жест, начатый с ручки, работает как прежде; поля живут как обычные поля.
  */
  const drag = useDragControls();

  /*
    На компьютере шторка — окно по центру: выезжать снизу из-за края монитора
    и тянуться за ручку там нечему. Появляется плавно, без пружины.
  */
  const desktop = useIsDesktop();

  const heightClass = {
    auto: 'max-h-[92dvh] lg:max-h-[86dvh]',
    half: 'h-[62dvh] lg:h-auto lg:max-h-[72dvh]',
    full: 'h-[94dvh] lg:h-[min(88dvh,920px)]',
  }[height];

  const motionProps = desktop
    ? {
        initial: { opacity: 0, y: 16, scale: 0.98 },
        animate: { opacity: 1, y: 0, scale: 1 },
        exit: { opacity: 0, y: 12, scale: 0.98 },
        transition: { duration: 0.18, ease: [0.16, 1, 0.3, 1] as const },
      }
    : {
        initial: { y: '100%' },
        animate: { y: 0 },
        exit: { y: '100%' },
        transition: { type: 'spring' as const, damping: 32, stiffness: 340 },
      };

  return createPortal(
    <AnimatePresence>
      {open && (
        // `data-sheet-open` нужен аппаратной кнопке «назад» в приложении: без
        // метки нажатие закрывало бы приложение целиком вместо этой шторки.
        <div
          className="fixed inset-0 z-50 flex items-end justify-center lg:items-center lg:p-6"
          data-sheet-open
        >
          <motion.div
            className="absolute inset-0 bg-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            aria-hidden
          />

          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={title}
            data-layout={desktop ? 'dialog' : 'sheet'}
            className={cn(
              'relative flex w-full max-w-lg flex-col',
              'rounded-t-2xl bg-surface shadow-lg',
              // На планшетах и в PWA-окне шторка не растягивается во всю ширину.
              'sm:mb-4 sm:rounded-2xl',
              'lg:mb-0 lg:max-w-xl',
              heightClass,
            )}
            {...motionProps}
            drag={desktop ? false : 'y'}
            dragControls={drag}
            dragListener={false}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.6 }}
            // Закрываем, если свайп достаточно длинный или быстрый.
            onDragEnd={(_, info) => {
              if (info.offset.y > 120 || info.velocity.y > 600) onClose();
            }}
          >
            {/* Ручка и заголовок — единственное, за что шторку тянут. */}
            <div
              className="shrink-0 touch-none select-none"
              onPointerDown={(event) => drag.start(event)}
            >
              {!desktop && (
                <div className="flex justify-center pt-2.5 pb-1" aria-hidden data-sheet-handle>
                  <span className="h-1 w-9 rounded-full bg-border-strong" />
                </div>
              )}

              {(title ?? description) && (
                <header className={cn('px-5 pb-3', desktop ? 'pt-5' : 'pt-2')}>
                  {title && <h2 className="text-lg font-semibold tracking-tight">{title}</h2>}
                  {description && <p className="mt-1 text-[13px] text-muted">{description}</p>}
                </header>
              )}
            </div>

            {/*
              Без панели действий последняя строка содержимого оказывается под
              системной панелью телефона: «Удалить» в списке действий задачи
              было видно наполовину и нажать его не получалось. Отступ снизу —
              по безопасной зоне, ровно как у панели действий.
            */}
            <div
              className={cn(
                'min-h-0 flex-1 overflow-y-auto overscroll-contain px-5',
                footer ? 'pb-4' : 'pb-[max(1rem,var(--safe-bottom))]',
              )}
            >
              {children}
            </div>

            {footer && (
              <footer className="border-t border-border bg-surface px-5 pt-3 pb-[max(1rem,var(--safe-bottom))]">
                {footer}
              </footer>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
